import { useMemo, useState } from 'react';

import type { RolePermissionGrid } from '../lib/api.js';

/**
 * WHAT EACH ROLE MAY DO IN THIS PRODUCTION.
 *
 * The catalogue is the default and a production is allowed to disagree — a
 * scout this one trusts to confirm a location, a UPM it keeps off the board —
 * so that saying so does not mean inventing a role.
 *
 * ONE ROLE AT A TIME, not 28 × 26 checkboxes. A matrix of 728 cells is a
 * screenshot, not a screen: nobody audits it, nobody finds the one they changed,
 * and every save is a race with whoever else has it open. Picking the role
 * first turns the question into the one a producer actually asks — "what may a
 * location scout do here" — and keeps the grid to one readable page.
 *
 * A CELL SAYS WHETHER IT IS THE DEFAULT. "Changed" is the thing a production
 * needs to see at a glance a month later, and a bare tick cannot say it.
 *
 * THE CONVERSATIONS ROW FOLLOWS THE SWITCH. While the platform has team
 * conversations off, nothing of the feature shows (ADR 0021), so its row and
 * its changed cells are left out here too. The server still sends them; the
 * overrides a production set before keep their state and return with the row.
 */
export function RolePermissionsGrid({
  grid,
  busy = false,
  error = null,
  conversationsOn = false,
  initialRole,
  onChange,
}: {
  grid: RolePermissionGrid;
  /** The role to open on — a member's, from the Team list. The first one otherwise. */
  initialRole?: string;
  busy?: boolean;
  error?: string | null;
  /** `features.conversations` from `/me`; unknown reads off, as everywhere else. */
  conversationsOn?: boolean;
  onChange: (cell: {
    role: string;
    permission: string;
    state: 'GRANTED' | 'REVOKED' | 'DEFAULT';
  }) => void;
}) {
  const [selected, setSelected] = useState(
    initialRole !== undefined && grid.roles.some((r) => r.role === initialRole)
      ? initialRole
      : (grid.roles[0]?.role ?? ''),
  );

  const role = useMemo(
    () => grid.roles.find((r) => r.role === selected) ?? grid.roles[0],
    [grid.roles, selected],
  );

  const byPermission = useMemo(
    () => new Map((role?.permissions ?? []).map((p) => [p.permission, p])),
    [role],
  );

  const features = useMemo(
    () => (conversationsOn ? grid.features : grid.features.filter((f) => f.feature !== 'CONVERSATIONS')),
    [grid.features, conversationsOn],
  );

  const shownPermissions = useMemo(
    () =>
      new Set(features.flatMap((f) => f.actions.flatMap((a) => a.cells.map((c) => c.permission)))),
    [features],
  );

  const changedCount = (role?.permissions ?? []).filter(
    (p) => p.state !== 'DEFAULT' && shownPermissions.has(p.permission),
  ).length;

  const departments = useMemo(() => {
    const seen = new Map<string, { label: string; roles: typeof grid.roles }>();
    for (const r of grid.roles) {
      const group = seen.get(r.department) ?? { label: r.departmentLabel, roles: [] };
      group.roles = [...group.roles, r];
      seen.set(r.department, group);
    }
    return [...seen.entries()];
  }, [grid.roles]);

  if (!role) return null;

  return (
    <section data-testid="role-permissions" style={{ marginTop: 28 }}>
      <p className="flbl" style={{ margin: 0, letterSpacing: '0.08em' }}>
        What each role may do here
      </p>
      <p className="mm" style={{ fontSize: 13, margin: '8px 0 0', lineHeight: 1.6, maxWidth: '68ch' }}>
        Every role starts from what that job normally does. Change it for this production only —
        nobody else&rsquo;s projects move.
      </p>

      {error === null ? null : (
        <p style={{ fontSize: 13, color: 'var(--danger)', marginTop: 12 }} data-testid="role-permissions-error">
          {error}
        </p>
      )}

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginTop: 16 }}>
        <label className="flbl" htmlFor="permission-role">
          Role
        </label>
        <select
          id="permission-role"
          data-testid="permission-role"
          value={role.role}
          onChange={(e) => {
            setSelected(e.target.value);
          }}
          style={{
            height: 40,
            border: '1px solid var(--color-divider)',
            padding: '0 10px',
            fontSize: 13,
            fontWeight: 600,
            background: 'var(--color-bg)',
          }}
        >
          {departments.map(([department, group]) => (
            <optgroup key={department} label={group.label}>
              {group.roles.map((r) => (
                <option key={r.role} value={r.role}>
                  {r.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        <span className="mm" style={{ fontSize: 12 }} data-testid="permission-role-summary">
          {role.summary}
        </span>
        {changedCount > 0 ? (
          <span className="stag" data-testid="permission-changed-count" style={{ marginLeft: 'auto' }}>
            {changedCount} changed from the default
          </span>
        ) : null}
      </div>

      <div style={{ overflowX: 'auto', marginTop: 16 }}>
        <table
          data-testid="permission-grid"
          style={{ borderCollapse: 'collapse', width: '100%', minWidth: 620, fontSize: 13 }}
        >
          <thead>
            <tr>
              <th style={th}>Feature</th>
              {grid.features[0]?.actions.map((a) => (
                <th key={a.action} style={th}>
                  {a.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {features.map((feature) => (
              <tr key={feature.feature} data-testid={`permission-row-${feature.feature}`}>
                <th scope="row" style={{ ...td, fontWeight: 700, textAlign: 'left' }}>
                  {feature.label}
                </th>
                {feature.actions.map((action) => (
                  <td key={action.action} style={td}>
                    {action.cells.length === 0 ? (
                      // "—", never an unticked box. An empty box invites a
                      // producer to tick it and wonder why nothing happened.
                      <span className="mm" aria-label="not applicable" style={{ opacity: 0.4 }}>
                        &mdash;
                      </span>
                    ) : (
                      <div style={{ display: 'grid', gap: 6 }}>
                        {action.cells.map((cell) => {
                          const current = byPermission.get(cell.permission);
                          const granted = current?.granted ?? false;
                          const changed = (current?.state ?? 'DEFAULT') !== 'DEFAULT';

                          return (
                            <label
                              key={cell.permission}
                              data-testid={`cell-${cell.permission}`}
                              data-changed={changed ? '1' : '0'}
                              style={{
                                display: 'flex',
                                gap: 8,
                                alignItems: 'baseline',
                                cursor: cell.locked || busy ? 'default' : 'pointer',
                                opacity: cell.locked ? 0.55 : 1,
                              }}
                            >
                              <input
                                type="checkbox"
                                data-testid={`toggle-${cell.permission}`}
                                checked={granted}
                                disabled={cell.locked || busy}
                                onChange={() => {
                                  // Back to DEFAULT rather than to the opposite
                                  // override when the click restores what the
                                  // catalogue says — a production should not
                                  // accumulate overrides that agree with it.
                                  const wanted = !granted;
                                  onChange({
                                    role: role.role,
                                    permission: cell.permission,
                                    state:
                                      wanted === (current?.byDefault ?? false)
                                        ? 'DEFAULT'
                                        : wanted
                                          ? 'GRANTED'
                                          : 'REVOKED',
                                  });
                                }}
                              />
                              <span>
                                {cell.label}
                                {cell.spends ? (
                                  <span
                                    className="mm"
                                    data-testid={`spends-${cell.permission}`}
                                    style={{ fontSize: 11, marginLeft: 6 }}
                                  >
                                    · spends
                                  </span>
                                ) : null}
                                {cell.locked ? (
                                  <span className="mm" style={{ fontSize: 11, marginLeft: 6 }}>
                                    · always on
                                  </span>
                                ) : null}
                                {changed ? (
                                  <span
                                    className="mm"
                                    data-testid={`changed-${cell.permission}`}
                                    style={{ fontSize: 11, marginLeft: 6, fontWeight: 700 }}
                                  >
                                    · changed
                                  </span>
                                ) : null}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 12px',
  borderBottom: '2px solid var(--ink)',
  fontSize: 11,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
};

const td: React.CSSProperties = {
  padding: '10px 12px',
  borderBottom: '1px solid var(--color-divider)',
  verticalAlign: 'top',
};
